import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { House, Mountains, Buildings, PaperPlaneTilt, CheckCircle, Camera, Lock, CalendarBlank, Crown, VideoCamera, Sparkle } from "@phosphor-icons/react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { celebrate } from "../lib/celebrate";
import { QuestProgress } from "./ui/quest-progress";
import { reeltyPackagePrice, sizeSurcharge, REELTY_PRICING } from "../lib/helpers";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const EMPTY = { street: "", city: "", state: "", zip: "", lat: null, lon: null, agent_id: "", photographer_id: "", pilot_id: "", shoot_date: "", shoot_time: "", status: "scheduled", notes: "", flat_fee: "", edit_fee: "", bonus: "", bonus_note: "", package_type: "All-Inclusive", interior: true, sqft: "", acres: "", drone: true, deliverables_url: "", crew_upload_url: "", waive_extras: false };
// 15-minute shoot start slots, 7 AM–5 PM Eastern (5 PM is the final start).
const fmt12 = (mins) => {
  const h = Math.floor(mins / 60), m = mins % 60;
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};
const TIME_SLOTS = [];
for (let mins = 7 * 60; mins <= 17 * 60; mins += 15) {
  const h = Math.floor(mins / 60), m = mins % 60;
  TIME_SLOTS.push({ value: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`, label: fmt12(mins) });
}

export default function PropertyDialog({ open, onClose, editing, users, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [discountCode, setDiscountCode] = useState("");
  const [discInfo, setDiscInfo] = useState(null); // { valid, discount_amount, new_total, message }
  const [discChecking, setDiscChecking] = useState(false);
  const isAdmin = user.role === "admin";
  const isStaff = user.role === "admin" || user.role === "employee";
  // Once booked, an agent may ONLY reschedule (date/time), and only 24h+ before the shoot.
  const lockedEdit = !!editing && !isStaff;
  const within24 = editing?.shoot_date ? (new Date(editing.shoot_date).getTime() - Date.now() < 24 * 3600 * 1000) : false;

  useEffect(() => {
    if (editing) {
      setForm({
        street: editing.street || editing.title || "", city: editing.city || "", state: editing.state || "",
        agent_id: editing.agent_id || "", photographer_id: editing.photographer_id || "",
        pilot_id: editing.pilot_id || "",
        shoot_date: editing.shoot_date ? editing.shoot_date.slice(0, 10) : "",
        shoot_time: editing.shoot_date ? new Date(editing.shoot_date).toTimeString().slice(0, 5) : "",
        status: editing.status || "scheduled", notes: editing.notes || "",
        flat_fee: editing.flat_fee ?? "", edit_fee: editing.edit_fee ?? "",
        zip: editing.zip || "",
        bonus: editing.bonus ?? "", bonus_note: editing.bonus_note || "",
        package_type: editing.package_type || "", drone: editing.drone || false, deliverables_url: editing.deliverables_url || "",
        crew_upload_url: editing.crew_upload_url || "",
        interior: editing.interior !== false, sqft: editing.sqft ?? "", acres: editing.acres ?? "",
        waive_extras: editing.partner_waived_extras || false,
      });
    } else setForm(EMPTY);
    setAddrQuery(""); setSuggestions([]); setShowSuggest(false); setStep(1);
    setDiscountCode(editing?.discount_code || ""); setDiscInfo(null);
  }, [editing, open]);

  const agents = users.filter((u) => u.role === "agent");
  const photogs = users.filter((u) => u.role === "photographer" || u.role === "dual" || (u.role === "pilot" && u.can_photograph));
  const pilots = users.filter((u) => u.role === "pilot" || u.role === "dual" || (u.role === "photographer" && u.can_pilot));
  const needsPhotog = form.package_type === "Photo Only" || (form.package_type === "All-Inclusive" && form.interior !== false);
  const needsPilot = form.package_type === "All-Inclusive" && (form.interior === false || !!form.drone);
  // Manually editing an address field invalidates any autocomplete-picked coordinates.
  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (["street", "city", "state", "zip"].includes(k)) { next.lat = null; next.lon = null; }
    return next;
  });

  const packagePrice = form.package_type ? reeltyPackagePrice(form.package_type, form.interior, form.sqft, form.acres) : 0;
  // Penthouse Partner pricing (the booking agent, or the selected agent when admin books).
  const bookingAgent = isStaff ? agents.find((a) => a.id === form.agent_id) : user;
  const isPartner = !!(bookingAgent && bookingAgent.penthouse_partner);
  const waiveActive = isPartner && form.package_type === "All-Inclusive" && !!form.waive_extras;
  const partnerPrice = (!isPartner || packagePrice <= 0) ? packagePrice
    : (form.package_type === "Photo Only" || waiveActive) ? Math.round(packagePrice * 0.75 * 100) / 100
    : packagePrice;
  const partnerSaved = Math.round((packagePrice - partnerPrice) * 100) / 100;
  const netPrice = discInfo?.valid ? Math.max(0, discInfo.new_total) : partnerPrice;

  // Live-validate the discount code against the current (partner-adjusted) package price.
  useEffect(() => {
    const code = discountCode.trim();
    if (!code || partnerPrice <= 0) { setDiscInfo(null); return; }
    let active = true;
    setDiscChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await client.post("/discounts/validate", { code, amount: partnerPrice });
        if (active) setDiscInfo(data.valid
          ? { valid: true, discount_amount: data.discount_amount, new_total: data.new_total, message: `${data.kind === "percent" ? data.value + "% off" : "$" + data.value + " off"} applied` }
          : { valid: false, message: data.message });
      } catch (e) { if (active) setDiscInfo({ valid: false, message: e.response?.data?.detail || "Couldn't check code" }); }
      finally { if (active) setDiscChecking(false); }
    }, 400);
    return () => { active = false; clearTimeout(t); };
  }, [discountCode, partnerPrice]);

  // ----- Address autocomplete (OpenStreetMap via backend proxy) -----
  const [addrQuery, setAddrQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const justPicked = useRef(false);

  useEffect(() => {
    const q = addrQuery.trim();
    // Skip the refetch triggered by writing the chosen label back into the box.
    if (justPicked.current) { justPicked.current = false; return; }
    if (q.length < 3) { setSuggestions([]); return; }
    let active = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await client.get("/geo/autocomplete", { params: { q } });
        if (active) { setSuggestions(data.results || []); setShowSuggest(true); }
      } catch { if (active) setSuggestions([]); }
      finally { if (active) setSearching(false); }
    }, 180);
    return () => { active = false; clearTimeout(t); };
  }, [addrQuery]);

  const pickSuggestion = (s) => {
    justPicked.current = true;
    setForm((f) => ({ ...f, street: s.street, city: s.city, state: s.state, zip: s.zip, lat: s.lat ?? null, lon: s.lon ?? null }));
    setAddrQuery(s.label);
    setSuggestions([]);
    setShowSuggest(false);
  };

  const save = async () => {
    if (lockedEdit) {
      if (within24) { toast.error("This shoot is within 24 hours. Contact Reelty to change the date or time."); return; }
      if (!form.shoot_date || !form.shoot_time) { toast.error("Please pick a shoot date and time"); return; }
      setSaving(true);
      try {
        await client.patch(`/properties/${editing.id}`, {
          shoot_date: new Date(`${form.shoot_date}T${form.shoot_time}:00`).toISOString(),
        });
        toast.success("Shoot rescheduled");
        onSaved();
        onClose();
      } catch (e) { toast.error(e.response?.data?.detail || "Couldn't reschedule"); }
      finally { setSaving(false); }
      return;
    }
    if (!form.street || !form.city || !form.state) { toast.error("Street address, city and state are required"); return; }
    if (!form.zip || !form.zip.trim()) { toast.error("A ZIP code is required for every shoot"); return; }
    if (!form.package_type) { toast.error("Please choose a package (Photo Only or All-Inclusive)"); return; }
    if (form.shoot_date && !form.shoot_time) { toast.error("Please pick a shoot time (15-min slots, 7 AM–5 PM ET)"); return; }
    setSaving(true);
    const body = {
      street: form.street.trim(), city: form.city.trim(), state: form.state.trim().toUpperCase(), zip: form.zip.trim(),
      lat: form.lat ?? null, lon: form.lon ?? null,
      status: form.status, notes: form.notes,
      shoot_date: form.shoot_date ? new Date(`${form.shoot_date}T${form.shoot_time || "07:00"}:00`).toISOString() : null,
      agent_id: form.agent_id || null, photographer_id: form.photographer_id || null,
      pilot_id: form.pilot_id || null,
      flat_fee: form.flat_fee !== "" ? Number(form.flat_fee) : null,
      edit_fee: form.edit_fee !== "" ? Number(form.edit_fee) : null,
      bonus: form.bonus !== "" ? Number(form.bonus) : null,
      bonus_note: form.bonus_note || null,
      package_type: form.package_type || null,
      interior: form.interior,
      drone: form.package_type === "All-Inclusive" ? (form.interior === false ? true : !!form.drone) : false,
      sqft: form.sqft !== "" ? Number(form.sqft) : null,
      acres: form.acres !== "" ? Number(form.acres) : null,
      deliverables_url: form.deliverables_url || null,
      crew_upload_url: form.crew_upload_url || null,
      discount_code: discInfo?.valid ? discountCode.trim().toUpperCase() : null,
      waive_extras: form.waive_extras || false,
    };
    try {
      if (editing) await client.patch(`/properties/${editing.id}`, body);
      else await client.post("/properties", body);
      if (!editing) celebrate();
      toast.success(editing ? "Shoot updated" : "Shoot scheduled! 📸");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  // Agents get a 2-page wizard (schedule → address); staff (admin/employee) see everything on one page.
  const showSchedule = lockedEdit || isStaff || step === 1;
  const showAddress = !lockedEdit && (isStaff || step === 2);
  const showPackage = !lockedEdit && (isStaff || step === 2);
  const goNext = () => {
    if (!form.shoot_date) { toast.error("Pick a shoot date"); return; }
    if (!form.shoot_time) { toast.error("Pick a shoot time"); return; }
    setStep(2);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto reelty-scroll bg-[#FBFAF7]" data-testid="property-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl">{lockedEdit ? "Reschedule shoot" : editing ? "Edit Shoot" : "New Shoot"}</DialogTitle><DialogDescription>{lockedEdit ? "Change the date and time. Address and package details are locked once booked." : isStaff ? "Enter the shoot address, schedule and assignment details." : (step === 1 ? "Step 1 of 2 · Schedule" : "Step 2 of 2 · Address & package")}</DialogDescription></DialogHeader>
        {!isStaff && !lockedEdit && (
          <div className="px-0.5 pt-1" data-testid="wizard-progress">
            <QuestProgress steps={[{ k: "sched", t: "Schedule" }, { k: "addr", t: "Address" }, { k: "book", t: "Book" }]}
              step={step - 1} testid="wizard-quest" />
          </div>
        )}
        <div className="space-y-4 py-2">
          {lockedEdit && (
            <div data-testid="locked-details-note" className="rounded-lg border border-[#E9DDC6] bg-[#FAF9F6] p-3.5">
              <p className="font-medium text-[#4A0000] flex items-center gap-1.5 text-sm"><Lock size={15} weight="fill" /> Booking details are locked</p>
              <p className="text-sm text-[#1A1A1A] mt-1.5">{editing.address}</p>
              <p className="text-sm text-[#5C5A58]">{editing.package_type}{editing.drone ? " · + Drone" : ""}</p>
              <p className="text-xs text-[#5C5A58] mt-2 leading-relaxed">
                {within24
                  ? "This shoot is within 24 hours, so the date and time are locked too. Contact Reelty to make a change."
                  : "You can update the date and time below (up to 24 hours before the shoot). To change the address or package, message an admin or contact Reelty."}
              </p>
            </div>
          )}
          {showAddress && (<>
          <div className="relative">
            <Label>{isAdmin ? "Search address (auto-fill)" : "Find the property address"}</Label>
            <Input
              data-testid="form-address-search"
              value={addrQuery}
              onChange={(e) => { setAddrQuery(e.target.value); setShowSuggest(true); }}
              onFocus={() => suggestions.length && setShowSuggest(true)}
              placeholder="Start typing an address…"
              className="bg-white border-[#E9DDC6]"
              autoComplete="off"
            />
            <p className="text-xs text-[#5C5A58] mt-1">
              {isAdmin ? "Pick a match to auto-fill the fields below — you can still edit them." : "Pick your address and we'll fill in the rest."}
            </p>
            {showSuggest && (searching || suggestions.length > 0) && (
              <div data-testid="address-suggestions" className="absolute z-50 left-0 right-0 mt-1 bg-white border border-[#E9DDC6] rounded shadow-lg max-h-64 overflow-y-auto">
                {searching && suggestions.length === 0 && (
                  <div className="px-3 py-2 text-sm text-[#5C5A58]">Searching…</div>
                )}
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`address-suggestion-${i}`}
                    onClick={() => pickSuggestion(s)}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-[#F5F1E8] border-b border-[#F0ECE2] last:border-0"
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div><Label>Street Address <span className="text-[#4A0000]">*</span></Label><Input data-testid="form-street" value={form.street} onChange={(e) => set("street", e.target.value)} placeholder="e.g. 1234 Washington Street" className="bg-white border-[#E9DDC6]" /><p className="text-xs text-[#5C5A58] mt-1">Becomes the shoot title.</p></div>
          <div className="grid grid-cols-6 gap-3">
            <div className="col-span-3"><Label>City <span className="text-[#4A0000]">*</span></Label><Input data-testid="form-city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Charlotte" className="bg-white border-[#E9DDC6]" /></div>
            <div className="col-span-1"><Label>State <span className="text-[#4A0000]">*</span></Label><Input data-testid="form-state" value={form.state} onChange={(e) => set("state", e.target.value.toUpperCase().slice(0, 2))} placeholder="NC" maxLength={2} className="bg-white border-[#E9DDC6] uppercase" /></div>
            <div className="col-span-2"><Label>ZIP <span className="text-[#4A0000]">*</span></Label><Input data-testid="form-zip" value={form.zip} onChange={(e) => set("zip", e.target.value)} placeholder="28139" className="bg-white border-[#E9DDC6] font-mono-num" /></div>
          </div>
          <p className="text-xs text-[#5C5A58] -mt-2">ZIP determines which photographers can claim this shoot.</p>
          <AnimatePresence>
            {form.street && form.city && form.state && form.zip && (
              <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                data-testid="address-locked-pill"
                className="flex items-center gap-1.5 text-xs font-medium text-[#2e7d32] bg-[#2e7d32]/10 border border-[#2e7d32]/25 rounded-[5px] px-3 py-1 w-fit">
                <CheckCircle size={13} weight="fill" /> Address locked in — looking sharp
              </motion.div>
            )}
          </AnimatePresence>
          </>)}
          {showSchedule && (<>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Shoot date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="form-date" disabled={lockedEdit && within24}
                      className="w-full flex items-center gap-2 h-10 px-3 rounded-md border border-[#E9DDC6] bg-white text-left text-sm disabled:opacity-60 disabled:cursor-not-allowed hover:border-[#4A0000]/40 transition-colors">
                      <CalendarBlank size={16} className="text-[#B08D57]" />
                      <span className={form.shoot_date ? "text-[#1A1A1A]" : "text-[#8A8782]"}>
                        {form.shoot_date ? new Date(form.shoot_date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "Pick a date"}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start" data-testid="form-date-calendar">
                    <Calendar mode="single"
                      selected={form.shoot_date ? new Date(form.shoot_date + "T00:00:00") : undefined}
                      onSelect={(d) => { if (d) set("shoot_date", `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`); }}
                      disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
                      classNames={{
                        caption_label: "font-display text-base text-[#4A0000]",
                        head_cell: "text-[#8A8782] rounded-md w-8 font-normal text-[0.8rem]",
                        day_selected: "bg-[#4A0000] text-white hover:bg-[#4A0000] hover:text-white focus:bg-[#4A0000] focus:text-white",
                        day_today: "bg-[#B08D57]/20 text-[#4A0000] font-semibold",
                        day: "h-8 w-8 p-0 font-normal rounded-md hover:bg-[#FAF9F6] aria-selected:opacity-100",
                      }} />
                  </PopoverContent>
                </Popover>
              </div>
              <div><Label>Shoot time <span className="text-[#5C5A58] font-normal text-xs">(ET)</span></Label>
                <Select value={form.shoot_time} onValueChange={(v) => set("shoot_time", v)} disabled={lockedEdit && within24}>
                  <SelectTrigger className="bg-white border-[#E9DDC6]" data-testid="form-time"><SelectValue placeholder="Pick a slot" /></SelectTrigger>
                  <SelectContent>{TIME_SLOTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-[#5C5A58] -mt-2">Every shoot runs 2 hours, between 7 AM and 5 PM Eastern.</p>
            {isStaff && (
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="bg-white border-[#E9DDC6]"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="scheduled">Scheduled</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="completed">Completed</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent>
                </Select>
              </div>
            )}
          </>)}
          {isStaff && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Agent</Label>
                <Select value={form.agent_id} onValueChange={(v) => set("agent_id", v)}>
                  <SelectTrigger className="bg-white border-[#E9DDC6]" data-testid="form-agent"><SelectValue placeholder="Assign agent" /></SelectTrigger>
                  <SelectContent>{agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {needsPhotog && (
              <div><Label>Photographer</Label>
                <Select value={form.photographer_id} onValueChange={(v) => set("photographer_id", v)}>
                  <SelectTrigger className="bg-white border-[#E9DDC6]" data-testid="form-photographer"><SelectValue placeholder="Assign photographer" /></SelectTrigger>
                  <SelectContent>{photogs.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || a.email}{a.role === "dual" ? " (dual)" : a.role === "pilot" ? " (pilot·dual)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              )}
              {needsPilot && (
              <div><Label className="flex items-center gap-1.5"><PaperPlaneTilt size={13} weight="fill" className="text-[#B08D57]" />Drone pilot</Label>
                <Select value={form.pilot_id} onValueChange={(v) => set("pilot_id", v)}>
                  <SelectTrigger className="bg-white border-[#E9DDC6]" data-testid="form-pilot"><SelectValue placeholder="Assign pilot" /></SelectTrigger>
                  <SelectContent>{pilots.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || a.email}{a.role === "dual" ? " (dual)" : a.role === "photographer" ? " (photog·dual)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              )}
            </div>
          )}
          {showPackage && (<>
          <div data-testid="package-picker">
            <Label>Your package <span className="text-[#4A0000]">*</span></Label>
            {form.package_type !== "Photo Only" ? (
              <>
                <motion.button type="button" data-testid="package-card-All-Inclusive"
                  onClick={() => set("package_type", "All-Inclusive")} whileTap={{ scale: 0.99 }}
                  className="relative w-full text-left rounded-xl border p-4 mt-1.5 overflow-hidden transition-all duration-200 border-[#4A0000] bg-[#4A0000] shadow-lg shadow-[#4A0000]/25">
                  <span className="absolute top-0 right-0 bg-[#B08D57] text-[#1A1715] text-[10px] font-bold tracking-[0.12em] uppercase px-2.5 py-1 rounded-bl-lg" data-testid="penthouse-badge">Most booked</span>
                  <div className="flex items-center gap-2.5">
                    <span className="grid place-items-center w-10 h-10 rounded-full bg-[#B08D57]/20"><Buildings size={22} weight="fill" className="text-[#B08D57]" /></span>
                    <div>
                      <div className="font-display text-lg leading-none text-white">All-Inclusive</div>
                      <div className="text-[10px] tracking-[0.16em] uppercase mt-1 text-[#B08D57]">Everything included · one visit</div>
                    </div>
                    <div className="ml-auto font-display text-2xl text-[#B08D57]">${REELTY_PRICING.allInclusive}</div>
                  </div>
                  <div className="mt-3.5 grid gap-1.5 text-sm text-white/85">
                    <span className="flex items-center gap-2"><CheckCircle size={15} weight="fill" className="text-[#B08D57] shrink-0" />Interior &amp; exterior photography</span>
                    <span className="flex items-center gap-2"><VideoCamera size={15} weight="fill" className="text-[#B08D57] shrink-0" />Cinematic video — branded &amp; unbranded</span>
                    <span className="flex items-center gap-2"><Sparkle size={15} weight="fill" className="text-[#B08D57] shrink-0" />Virtual staging &amp; twilight edits</span>
                  </div>
                </motion.button>
                {isPartner ? (
                  <div className="mt-2 rounded-lg border border-[#B08D57]/55 bg-[#B08D57]/12 px-3 py-2.5" data-testid="partner-callout">
                    <div className="flex items-start gap-2.5">
                      <Crown size={18} weight="fill" className="text-[#B08D57] mt-0.5 shrink-0" />
                      <p className="text-xs leading-relaxed text-[#7a5c1a]"><b className="text-[#4A0000]">Penthouse Partner</b> — your <b>360° tour is included free</b> on this shoot.</p>
                    </div>
                    <label className="flex items-center gap-2 text-xs mt-2.5 cursor-pointer text-[#5C5A58]" data-testid="partner-waive-label">
                      <input type="checkbox" data-testid="partner-waive" checked={!!form.waive_extras} onChange={(e) => set("waive_extras", e.target.checked)} className="w-4 h-4 accent-[#4A0000]" />
                      Not using it this time? Forgo the 360° tour for <b className="text-[#4A0000]">25% off</b> <span className="text-[#2e7d32] font-medium">(save ${Math.round(packagePrice * 0.25 * 100) / 100})</span>
                    </label>
                  </div>
                ) : (
                  <div className="mt-2 rounded-lg border border-[#B08D57]/45 bg-[#B08D57]/10 px-3 py-2.5 flex items-start gap-2.5" data-testid="penthouse-partner-callout">
                    <Crown size={18} weight="fill" className="text-[#B08D57] mt-0.5 shrink-0" />
                    <p className="text-xs leading-relaxed text-[#7a5c1a]">
                      <b className="text-[#4A0000]">360° tour included free</b> with Penthouse Partner — your <b>1st month is on us</b>. Not using it? Skip for <b>25% off</b>. A total no-brainer.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <motion.div data-testid="package-card-Photo Only" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  className="relative w-full rounded-xl border p-4 mt-1.5 border-[#4A0000] bg-[#4A0000] text-white">
                  <div className="flex items-center gap-2.5">
                    <span className="grid place-items-center w-10 h-10 rounded-full bg-[#B08D57]/20"><House size={22} weight="fill" className="text-[#B08D57]" /></span>
                    <div>
                      <div className="font-display text-lg leading-none">Photo Only</div>
                      <div className="text-[11px] mt-1 text-white/70">30–50 professional photos</div>
                    </div>
                    <div className="ml-auto text-right">
                      {isPartner ? (
                        <>
                          <div className="font-display text-2xl text-[#B08D57] leading-none">${Math.round(REELTY_PRICING.photoOnly * 0.75 * 100) / 100}</div>
                          <div className="text-[10px] tracking-[0.1em] uppercase text-white/60 mt-1">Partner · 25% off</div>
                        </>
                      ) : (
                        <div className="font-display text-2xl text-[#B08D57]">${REELTY_PRICING.photoOnly}</div>
                      )}
                    </div>
                  </div>
                </motion.div>
                <button type="button" data-testid="back-to-penthouse" onClick={() => set("package_type", "All-Inclusive")}
                  className="mt-2.5 inline-flex items-center gap-1 text-sm text-[#4A0000] hover:text-[#5C1A1A] font-medium transition-colors">
                  <Crown size={14} weight="fill" className="text-[#B08D57]" />Get video, staging + free 360 &amp; drone → All-Inclusive
                </button>
              </>
            )}
          </div>
          {/* Skip interior (drone / exterior only) → auto −$135 on All-Inclusive */}
          <AnimatePresence>
            {form.package_type === "All-Inclusive" && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                <button type="button" data-testid="form-interior" onClick={() => set("interior", !form.interior)}
                  className={`w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-all ${!form.interior ? "border-[#B08D57] bg-[#B08D57]/15 shadow-sm" : "border-[#E9DDC6] bg-white hover:border-[#B08D57]"}`}>
                  <span className={`flex-shrink-0 grid place-items-center w-9 h-9 rounded-full ${!form.interior ? "bg-[#B08D57] text-white" : "bg-[#FAF9F6] text-[#4A0000]"}`}>
                    <PaperPlaneTilt size={18} weight={!form.interior ? "fill" : "duotone"} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-display text-base leading-none text-[#1A1A1A]">Skip interior — drone / exterior only</span>
                    <span className="block text-xs text-[#5C5A58] mt-0.5">Land, construction or aerials only? {form.interior ? <span className="text-[#8A6D1B] font-medium">Save ${REELTY_PRICING.noInterior}</span> : <span className="text-[#2e7d32] font-medium">−${REELTY_PRICING.noInterior} applied</span>}</span>
                  </span>
                  <span className={`flex-shrink-0 relative w-11 h-6 rounded-full transition-colors ${!form.interior ? "bg-[#4A0000]" : "bg-[#D9D4C8]"}`}>
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${!form.interior ? "left-[22px]" : "left-0.5"}`} />
                  </span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          {/* Drone / aerials — optional on All-Inclusive; adds a pilot to the shoot */}
          <AnimatePresence>
            {form.package_type === "All-Inclusive" && form.interior !== false && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                <button type="button" data-testid="form-drone" onClick={() => set("drone", !form.drone)}
                  className={`w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-all ${form.drone ? "border-[#B08D57] bg-[#B08D57]/15 shadow-sm" : "border-[#E9DDC6] bg-white hover:border-[#B08D57]"}`}>
                  <span className={`flex-shrink-0 grid place-items-center w-9 h-9 rounded-full ${form.drone ? "bg-[#B08D57] text-white" : "bg-[#FAF9F6] text-[#4A0000]"}`}>
                    <PaperPlaneTilt size={18} weight={form.drone ? "fill" : "duotone"} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-display text-base leading-none text-[#1A1A1A]">Drone / aerials</span>
                    <span className="block text-xs text-[#5C5A58] mt-0.5">{form.drone ? <span className="text-[#2e7d32] font-medium">Included — a drone pilot will be assigned</span> : "Add aerial photos/video — assigns a drone pilot"}</span>
                  </span>
                  <span className={`flex-shrink-0 relative w-11 h-6 rounded-full transition-colors ${form.drone ? "bg-[#4A0000]" : "bg-[#D9D4C8]"}`}>
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${form.drone ? "left-[22px]" : "left-0.5"}`} />
                  </span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          {/* Property size — surcharge only for large properties */}
          {form.package_type && (
            <div className="grid grid-cols-2 gap-3" data-testid="size-fields">
              <div><Label>Building sqft <span className="text-[#5C5A58] font-normal text-xs">(optional)</span></Label><Input data-testid="form-sqft" type="number" value={form.sqft} onChange={(e) => set("sqft", e.target.value)} placeholder="e.g. 3200" className="bg-white border-[#E9DDC6] font-mono-num" /></div>
              <div><Label>Lot acres <span className="text-[#5C5A58] font-normal text-xs">(optional)</span></Label><Input data-testid="form-acres" type="number" value={form.acres} onChange={(e) => set("acres", e.target.value)} placeholder="e.g. 0.5" className="bg-white border-[#E9DDC6] font-mono-num" /></div>
              <p className="col-span-2 text-xs text-[#5C5A58] -mt-1">Up to 5,000 sqft / 50 ac included. Larger properties add one flat +${REELTY_PRICING.oversized} (unlimited sqft / acreage).</p>
            </div>
          )}
          {form.package_type && (
            <div data-testid="discount-code-field">
              <Label>Discount code <span className="text-[#5C5A58] font-normal text-xs">(optional)</span></Label>
              <Input data-testid="form-discount-code" value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                placeholder="e.g. SAVE20" className="bg-white border-[#E9DDC6] font-mono-num uppercase" />
              {discChecking && <p className="text-xs text-[#5C5A58] mt-1" data-testid="discount-checking">Checking…</p>}
              {!discChecking && discInfo && (
                <p data-testid="discount-message" className={`text-xs mt-1 ${discInfo.valid ? "text-green-700" : "text-red-600"}`}>{discInfo.message}</p>
              )}
            </div>
          )}
          </>)}
          {isAdmin && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Flat fee ($)</Label><Input data-testid="form-fee" type="number" value={form.flat_fee} onChange={(e) => set("flat_fee", e.target.value)} placeholder="auto from tier" className="bg-white border-[#E9DDC6] font-mono-num" /></div>
              <div><Label>Edit fee ($)</Label><Input data-testid="form-editfee" type="number" value={form.edit_fee} onChange={(e) => set("edit_fee", e.target.value)} className="bg-white border-[#E9DDC6] font-mono-num" /></div>
            </div>
          )}
          {isAdmin && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Add-on / promo ($)</Label><Input data-testid="form-bonus" type="number" value={form.bonus} onChange={(e) => set("bonus", e.target.value)} placeholder="0" className="bg-white border-[#E9DDC6] font-mono-num" /></div>
              <div><Label>Add-on reason</Label><Input data-testid="form-bonus-note" value={form.bonus_note} onChange={(e) => set("bonus_note", e.target.value)} placeholder="Rush, loyalty, etc." className="bg-white border-[#E9DDC6]" /></div>
            </div>
          )}
          {editing && showPackage && isStaff && (
            <div><Label>Deliverables link (agent-facing · Dropbox / DocSend)</Label><Input data-testid="form-deliverables" value={form.deliverables_url} onChange={(e) => set("deliverables_url", e.target.value)} placeholder="https://www.dropbox.com/…" className="bg-white border-[#E9DDC6]" /><p className="text-xs text-[#5C5A58] mt-1">What the agent sees. Add this once media is ready.</p></div>
          )}
          {editing && showPackage && isStaff && (
            <div><Label className="flex items-center gap-1.5"><PaperPlaneTilt size={13} weight="fill" className="text-[#B08D57]" />Crew upload link (photographer / pilot)</Label><Input data-testid="form-crew-upload" value={form.crew_upload_url} onChange={(e) => set("crew_upload_url", e.target.value)} placeholder="https://…/upload" className="bg-white border-[#E9DDC6]" /><p className="text-xs text-[#5C5A58] mt-1">Where crew upload their raw files — separate from the agent's deliverables link.</p></div>
          )}
          {showPackage && (<>
          <div><Label>Notes</Label><Textarea data-testid="form-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-white border-[#E9DDC6]" /></div>
          </>)}
          {form.package_type && !lockedEdit && (
            <motion.div key={packagePrice} initial={{ scale: 0.97, opacity: 0.6 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 420, damping: 22 }}
              data-testid="sticky-price-bar"
              className="font-display sticky bottom-0 z-20 mt-1 rounded-[5px] bg-[#FBFAF7]/85 backdrop-blur-xl backdrop-saturate-150 border border-[#4A0000]/[0.12] px-4 py-3 shadow-[0_-8px_28px_-12px_rgba(74,0,0,0.25)]">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#5C5A58]">Total</span>
                <span className="flex items-center gap-2">
                  {(discInfo?.valid || partnerSaved > 0) && packagePrice > 0 && (
                    <span className="font-display text-base text-[#5C5A58] line-through">${packagePrice}</span>
                  )}
                  <span data-testid="package-price-display" className="font-display text-2xl text-[#4A0000]">{form.package_type ? `$${netPrice}` : "—"}</span>
                </span>
              </div>
              {isPartner && partnerSaved > 0 && (
                <div className="flex items-center justify-between text-xs text-[#2e7d32] mt-1 pt-1 border-t border-[#4A0000]/10" data-testid="partner-saved-row">
                  <span className="flex items-center gap-1"><Crown size={12} weight="fill" className="text-[#B08D57]" /> Penthouse Partner {waiveActive ? "(waived 360° tour)" : "rate"}</span>
                  <span className="font-display">−${partnerSaved}</span>
                </div>
              )}
              {isPartner && !waiveActive && form.package_type === "All-Inclusive" && (
                <div className="flex items-center justify-between text-xs text-[#8A6D1B] mt-1 pt-1 border-t border-[#4A0000]/10" data-testid="partner-perks-row">
                  <span className="flex items-center gap-1"><Crown size={12} weight="fill" className="text-[#B08D57]" /> 360° tour</span>
                  <span className="font-display">included free</span>
                </div>
              )}
              {form.package_type === "All-Inclusive" && !form.interior && (
                <div className="flex items-center justify-between text-xs text-[#2e7d32] mt-1 pt-1 border-t border-[#4A0000]/10">
                  <span className="flex items-center gap-1"><PaperPlaneTilt size={12} weight="fill" /> No interior — drone / exterior only</span>
                  <span className="font-display">−${REELTY_PRICING.noInterior}</span>
                </div>
              )}
              {sizeSurcharge(form.sqft, form.acres) > 0 && (
                <div className="flex items-center justify-between text-xs text-[#8A6D1B] mt-1 pt-1 border-t border-[#4A0000]/10">
                  <span>Oversized property fee</span>
                  <span className="font-display">+${sizeSurcharge(form.sqft, form.acres)}</span>
                </div>
              )}
              {discInfo?.valid && (
                <div className="flex items-center justify-between text-xs text-[#2e7d32] mt-1 pt-1 border-t border-[#4A0000]/10" data-testid="discount-applied-row">
                  <span>Discount {discountCode.trim().toUpperCase()}</span>
                  <span className="font-display">−${discInfo.discount_amount}</span>
                </div>
              )}
            </motion.div>
          )}
        </div>
        <DialogFooter>
          {!isAdmin && !lockedEdit && step === 2 && (
            <Button variant="outline" data-testid="wizard-back" onClick={() => setStep(1)} className="border-[#E9DDC6] mr-auto">Back</Button>
          )}
          <Button variant="outline" onClick={onClose} className="border-[#E9DDC6]">Cancel</Button>
          {!isAdmin && !lockedEdit && step === 1 ? (
            <Button data-testid="wizard-next" onClick={goNext} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">Next: Address</Button>
          ) : (
            <Button data-testid="form-save" onClick={save} disabled={saving || (lockedEdit && within24)} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white group">
              <Camera size={17} weight="fill" className="mr-1.5 transition-transform group-hover:scale-110 group-hover:rotate-6" />
              {saving ? (lockedEdit ? "Rescheduling…" : editing ? "Saving…" : "Scheduling…") : (lockedEdit ? "Reschedule" : editing ? "Save Shoot" : "Schedule Shoot")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
